// AI Alert Module Types and Interfaces

export type AlertSeverity = 'CRITICAL' | 'HIGH' | 'MEDIUM' | 'LOW'
export type AlertType =
  | 'hypoglycemia'
  | 'critical_hypoglycemia'
  | 'hyperglycemia'
  | 'rapid_drop'
  | 'pattern_detected'
  | 'somogyi'
  | 'trend_analysis'
  | 'system'

/**
 * Standardized alert severity colors - use these throughout the app
 * for consistent visual representation of alert severity levels
 */
export const ALERT_SEVERITY_COLORS: Record<AlertSeverity | 'default', string> = {
  CRITICAL: '#dc3545', // Red
  HIGH: '#fd7e14', // Orange
  MEDIUM: '#ffc107', // Yellow/Amber
  LOW: '#20c997', // Teal/Green
  default: '#6c757d', // Gray
}

/**
 * Get the color for a given alert severity level
 * Handles both uppercase (CRITICAL) and lowercase (critical) inputs
 * @param severity - The alert severity (CRITICAL/critical, HIGH/high, etc.)
 * @returns The hex color string for that severity
 */
export const getAlertSeverityColor = (severity: AlertSeverity | string): string => {
  const normalizedSeverity = severity.toUpperCase()
  if (normalizedSeverity in ALERT_SEVERITY_COLORS) {
    return ALERT_SEVERITY_COLORS[normalizedSeverity as AlertSeverity]
  }
  return ALERT_SEVERITY_COLORS.default
}
export type PatternType = 'somogyi' | 'dawn_phenomenon' | 'trend_analysis' | 'insulin_resistance'
export type NotificationChannel = 'dashboard' | 'email' | 'push' | 'sms'
export type AlertStatus = 'active' | 'acknowledged' | 'resolved' | 'dismissed'

// Core Alert Interfaces
export interface AlertRule {
  id: number
  pet_id: number
  rule_type: string
  threshold_value: number
  threshold_unit: string
  is_active: boolean
  created_by: number
  created_at: Date
  updated_at: Date
}

export interface AlertEvent {
  id?: number
  pet_id: number
  device_id: number
  alert_type: AlertType
  severity: AlertSeverity
  title: string
  message: string
  glucose_value?: number
  recommendations: string[]
  triggered_at: Date
  acknowledged_at?: Date
  acknowledged_by?: number
  metadata?: Record<string, any>
  status: AlertStatus
}

export interface PatternDetection {
  id?: number
  pet_id: number
  pattern_type: PatternType
  confidence_score: number // 0.0 - 1.0
  detection_window_start: Date
  detection_window_end: Date
  analysis_data: Record<string, any>
  created_at: Date
}

export interface DailyAlertReport {
  id?: number
  pet_id: number
  report_date: Date
  total_alerts: number
  critical_alerts: number
  patterns_detected: PatternDetection[]
  trend_analysis: TrendAnalysis
  recommendations: string[]
  generated_at: Date
}

// Specialized Alert Types
export interface HypoglycemiaAlert extends Omit<AlertEvent, 'alert_type'> {
  alert_type: 'hypoglycemia'
  glucose_threshold: number
  duration_below_threshold?: number // minutes
  previous_episodes_today?: number
}

export interface SomogyiAlert extends Omit<AlertEvent, 'alert_type'> {
  alert_type: 'somogyi'
  pattern_detection: {
    nighttime_low: {
      timestamp: Date
      glucose_value: number
    }
    morning_high: {
      timestamp: Date
      glucose_value: number
    }
    rebound_magnitude: number
    confidence_score: number
  }
}

// Trend Analysis Types
export interface GlucoseVariabilityMetrics {
  mean: number
  standard_deviation: number
  coefficient_of_variation: number
  classification: 'stable' | 'variable' | 'highly_variable'
}

export interface TimeInRangeMetrics {
  target_range_percentage: number // 3.9-10.0 mmol/L
  below_range_percentage: number // <3.9 mmol/L
  above_range_percentage: number // >10.0 mmol/L
  very_low_percentage: number // <3.0 mmol/L
  very_high_percentage: number // >13.9 mmol/L
}

export interface InsulinEffectivenessAssessment {
  post_injection_response: 'effective' | 'insufficient' | 'excessive'
  average_response_time: number // minutes
  peak_effect_timing: number // minutes
  duration_of_action: number // hours
  dosage_recommendation: 'maintain' | 'increase' | 'decrease' | 'adjust_timing'
}

export interface DietExerciseImpact {
  meal_response_patterns: {
    pre_meal_average: number
    post_meal_peak: number
    return_to_baseline_time: number // minutes
  }
  exercise_response_patterns: {
    during_exercise_trend: 'stable' | 'declining' | 'rising'
    post_exercise_effect: 'hypoglycemia_risk' | 'normal' | 'hyperglycemia'
    recovery_time: number // minutes
  }
  anomalies_detected: string[]
}

export interface TrendAnalysis {
  analysis_period: {
    start_date: Date
    end_date: Date
    total_readings: number
  }
  glucose_control_assessment: 'excellent' | 'good' | 'fair' | 'poor'
  variability_metrics: GlucoseVariabilityMetrics
  time_in_range_metrics: TimeInRangeMetrics
  insulin_assessment: InsulinEffectivenessAssessment
  diet_exercise_impact: DietExerciseImpact
  recommendations: string[]
  confidence_score: number
}

// Notification Types
export interface NotificationPreference {
  user_id: number
  channel: NotificationChannel
  enabled: boolean
  alert_types: AlertType[]
  quiet_hours?: {
    start_time: string // HH:MM format
    end_time: string // HH:MM format
  }
}

export interface NotificationLog {
  id?: number
  alert_event_id: number
  recipient_type: 'vet' | 'owner' | 'mobile_app'
  recipient_id: string
  delivery_channel: NotificationChannel
  status: 'sent' | 'delivered' | 'failed' | 'pending'
  sent_at: Date
  delivered_at?: Date
  failure_reason?: string
}

// Alert Engine Configuration
export interface AlertEngineConfig {
  hypoglycemia_thresholds: {
    critical: number // e.g., 3.0 mmol/L
    high: number // e.g., 3.9 mmol/L
    medium: number // e.g., 5.0 mmol/L
  }
  hyperglycemia_thresholds: {
    high: number // e.g., 13.9 mmol/L
    medium: number // e.g., 10.0 mmol/L
  }
  somogyi_detection: {
    enabled: boolean
    sensitivity: 'low' | 'medium' | 'high'
    minimum_confidence: number
    nighttime_window: { start: string; end: string } // HH:MM format
    morning_window: { start: string; end: string }
    minimum_rebound_magnitude: number
  }
  trend_analysis: {
    enabled: boolean
    analysis_window_days: number
    minimum_readings_required: number
    cv_threshold: number // Coefficient of variation threshold
  }
}

// API Response Types
export interface AlertAPIResponse {
  success: boolean
  data?: any
  error?: string
  timestamp: Date
}

export interface AlertQueryParams {
  pet_id?: number
  start_date?: Date
  end_date?: Date
  severity?: AlertSeverity[]
  alert_type?: AlertType[]
  status?: AlertStatus[]
  limit?: number
  offset?: number
}

// Real-time Alert Subscription
export interface AlertSubscription {
  pet_id: number
  alert_types: AlertType[]
  callback: (alert: AlertEvent) => void
  unsubscribe: () => void
}

// Chart Integration Types
export interface PatternMarker {
  id: string
  pattern_type: PatternType
  start_timestamp: Date
  end_timestamp: Date
  confidence_score: number
  description: string
  highlight_color: string
}

// Utility Types
export type AlertEventWithPatterns = AlertEvent & {
  patterns?: PatternDetection[]
  related_alerts?: AlertEvent[]
}

export type PetAlertSummary = {
  pet_id: number
  last_24h: {
    total_alerts: number
    critical_alerts: number
    latest_alert?: AlertEvent
  }
  last_7d: {
    total_alerts: number
    patterns_detected: number
    trend_analysis?: TrendAnalysis
  }
  active_alerts: AlertEvent[]
}

// Component Props Types
export interface AlertDisplayProps {
  alerts: AlertEvent[]
  onAlertClick?: (alert: AlertEvent) => void
  onAlertAcknowledge?: (alertId: number) => void
  showSeverityFilter?: boolean
  showTypeFilter?: boolean
}

export interface TrendAnalysisDisplayProps {
  analysis: TrendAnalysis
  showRecommendations?: boolean
  compact?: boolean
}
